// frontend/src/pages/looks/AllLooksPage.js
// W3 — Public, paginated "All Looks" page. Supports ?shopId= or ?staffId=
// (and optional &page=N). Looks are compact horizontal cards (image left,
// info right) that pack ~5 per row on desktop and become single-row cards
// with a small thumbnail on phones. Typography matches the rest of the app.
// The public list API is enriched so each card can show the professional's
// name, business, service, duration, fee and a "Book This Look" action.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FaClock, FaTags, FaRegImages } from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import "./AllLooksPage.css";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
const PAGE_SIZE = 18;

function AllLooksPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get("shopId") || "";
  const staffId = searchParams.get("staffId") || "";
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1;

  const [looks, setLooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadLooks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (shopId) params.set("shopId", shopId);
      if (staffId) params.set("staffId", staffId);
      params.set("page", String(page));
      params.set("limit", String(PAGE_SIZE));
      const res = await fetch(`${DEFAULT_API}/api/looks/public?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load looks");
      setLooks(data.looks || []);
      setTotal(Number(data.total) || 0);
      setHasMore(!!data.hasMore);
    } catch (err) {
      setError(err.message || "Failed to load looks");
      setLooks([]);
    } finally {
      setLoading(false);
    }
  }, [shopId, staffId, page]);

  useEffect(() => {
    loadLooks();
  }, [loadLooks]);

  const firstName = looks[0]?.staffName;
  const businessName = looks[0]?.businessName;
  const staffSlug = looks[0]?.staffSlug;

  const handleBook = (look) => {
    const params = new URLSearchParams();
    if (look?.staffId) params.set("staffId", String(look.staffId));
    if (look?.serviceId) params.set("serviceId", String(look.serviceId));
    if (look?._id) params.set("lookId", String(look._id));
    if (look?.shopId) params.set("shopId", String(look.shopId));
    const isFreelancer = look?.staffRole === "R4" || look?.staffRole === "R7";
    navigate(
      `${isFreelancer ? "/freelancer-booking" : "/booking"}?${params.toString()}`,
    );
  };

  const goToPage = (nextPage) => {
    const params = new URLSearchParams();
    if (shopId) params.set("shopId", shopId);
    if (staffId) params.set("staffId", staffId);
    if (nextPage > 1) params.set("page", String(nextPage));
    navigate(`/looks/all?${params.toString()}`);
    window.scrollTo({ top: 0 });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const subtitle = staffId
    ? `All published looks by ${firstName || "this professional"}`
    : shopId
      ? `All published looks at ${businessName || "this location"}`
      : "All published looks across OGESTYLE";

  const fmtPrice = (n) => {
    const num = Number(n);
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  };

  const priceLabel = (look) => {
    const price = Number.isFinite(Number(look?.price)) ? Number(look.price) : null;
    if (price === null) return null;
    const pt = look?.priceType || "from";
    if (pt === "variable") return "Price varies";
    if (pt === "hourly") return `$${fmtPrice(price)}/hr`;
    if (pt === "range" && Number.isFinite(Number(look?.maxPrice)))
      return `$${fmtPrice(price)} – $${fmtPrice(look.maxPrice)}`;
    if (pt === "fixed") return `$${fmtPrice(price)}`;
    return `From $${fmtPrice(price)}`;
  };

  const roleLabel = (role) =>
    role === "R4" ? "Freelancer" : role === "R7" ? "Booth Renter" : "Professional";

  const renderCard = (look) => {
    const images = Array.isArray(look?.images) ? look.images : [];
    const mainImage = images.length > 0 ? images[0]?.url : null;
    const imageCount = images.length;
    const serviceName = look?.bookingSnapshot?.serviceName || "Beauty Look";
    const proName = look?.staffName || "Professional";
    const biz = look?.businessName || "";
    const duration = look?.durationMinutes || null;
    const fee = priceLabel(look);
    const initials =
      proName
        .split(" ")
        .map((s) => s?.[0] || "")
        .join("")
        .slice(0, 2)
        .toUpperCase() || "OG";
    const lookUrl = `/look/${look._id}`;

    return (
      <article className="alllooks-card" key={look._id}>
        <Link to={lookUrl} className="alllooks-media" aria-label={`View ${serviceName} by ${proName}`}>
          {mainImage ? (
            <img src={mainImage} alt={serviceName} loading="lazy" />
          ) : (
            <span className="alllooks-media-empty">{serviceName}</span>
          )}
          {imageCount > 1 && (
            <span className="alllooks-imgcount">
              <FaRegImages /> {imageCount}
            </span>
          )}
        </Link>

        <div className="alllooks-info">
          <div className="alllooks-pro">
            {look?.staffProfileImage ? (
              <img className="alllooks-avatar" src={look.staffProfileImage} alt="" />
            ) : (
              <span className="alllooks-avatar alllooks-avatar--initials">{initials}</span>
            )}
            <div className="alllooks-protext">
              <span className="alllooks-name" title={proName}>
                {proName}
              </span>
              <span className="alllooks-prometa" title={biz || roleLabel(look?.staffRole)}>
                {biz || roleLabel(look?.staffRole)}
              </span>
            </div>
          </div>

          <h3 className="alllooks-service" title={serviceName}>
            {serviceName}
          </h3>

          <div className="alllooks-meta">
            {duration ? (
              <span className="alllooks-meta-item">
                <FaClock /> {duration} min
              </span>
            ) : null}
            {fee ? (
              <span className="alllooks-meta-item alllooks-meta-item--fee">
                <FaTags /> {fee}
              </span>
            ) : null}
          </div>

          <button type="button" className="alllooks-book" onClick={() => handleBook(look)}>
            Book This Look
          </button>
        </div>
      </article>
    );
  };

  return (
    <div className="alllooks-page">
      <CustomerHeader />
      <main className="alllooks-main">
        <p className="alllooks-topline">
          {subtitle}
          {!loading && total > 0 && (
            <span className="alllooks-count">
              {" "}
              · {total.toLocaleString()} look{total === 1 ? "" : "s"}
            </span>
          )}
        </p>

        {error && <div className="alllooks-error">{error}</div>}

        {loading ? (
          <div className="alllooks-state">Loading looks…</div>
        ) : looks.length === 0 ? (
          <div className="alllooks-state">No looks available yet.</div>
        ) : (
          <div className="alllooks-list">{looks.map(renderCard)}</div>
        )}

        {!loading && looks.length > 0 && (
          <div className="alllooks-pager">
            <button
              type="button"
              className="alllooks-pagebtn"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
            >
              ← Prev
            </button>
            <span className="alllooks-pagenum">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="alllooks-pagebtn"
              onClick={() => goToPage(page + 1)}
              disabled={!hasMore}
            >
              Next →
            </button>
          </div>
        )}
      </main>
      <CustomerFooter />
    </div>
  );
}

export default AllLooksPage;
